/**
 * Centralized tag management composable for PALLAS
 *
 * Provides dynamic tag fetching from Firestore with static fallback.
 * Single source of truth for all tag operations across the app.
 *
 * Usage:
 *   import { useTags } from '@/composables/useTags'
 *   const { tags, artTags, scienceTags, getTagTheme, isLoading } = useTags()
 *
 * For admin operations:
 *   import { useTagsAdmin } from '@/composables/useTags'
 *   const { addTag, removeTag, updateTag } = useTagsAdmin()
 */

import { ref, computed, readonly } from 'vue'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { PREDEFINED_TAGS, ART_TAGS, SCIENCE_TAGS, getTagTheme as staticGetTagTheme } from '@/config/tags.js'

// Singleton state - shared across all component instances
const tags = ref([])
const isLoading = ref(false)
const isLoaded = ref(false)
const loadError = ref(null)
const usingFallback = ref(false)

// Firestore document path
const TAGS_DOC_PATH = 'siteContent/tags'

/**
 * Load tags from Firestore, falling back to static tags if needed
 */
async function loadTags(forceReload = false) {
  // Skip if already loaded and not forcing reload
  if (isLoaded.value && !forceReload) {
    return tags.value
  }

  // Skip if currently loading
  if (isLoading.value) {
    // Wait for existing load to complete
    return new Promise((resolve) => {
      const checkLoaded = setInterval(() => {
        if (!isLoading.value) {
          clearInterval(checkLoaded)
          resolve(tags.value)
        }
      }, 50)
    })
  }

  isLoading.value = true
  loadError.value = null

  try {
    const docRef = doc(db, TAGS_DOC_PATH)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      const data = docSnap.data()
      if (data.tags && Array.isArray(data.tags) && data.tags.length > 0) {
        // Sort tags alphabetically by name for consistent display
        tags.value = [...data.tags].sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        )
        usingFallback.value = false
      } else {
        // Empty or invalid tags array - use fallback
        tags.value = [...PREDEFINED_TAGS]
        usingFallback.value = true
      }
    } else {
      // Document doesn't exist - use fallback
      tags.value = [...PREDEFINED_TAGS]
      usingFallback.value = true
    }
  } catch (error) {
    loadError.value = error.message
    // Use static fallback on error
    tags.value = [...PREDEFINED_TAGS]
    usingFallback.value = true
  } finally {
    isLoading.value = false
    isLoaded.value = true
  }

  return tags.value
}

/**
 * Get tag theme - uses dynamic tags if loaded, falls back to static lookup
 */
function getTagTheme(tagName) {
  if (!tagName) return 'art'

  const lowerTagName = tagName.toLowerCase()

  // First check dynamic tags
  if (tags.value.length > 0) {
    const tag = tags.value.find(t => t.name.toLowerCase() === lowerTagName)
    if (tag) return tag.theme
  }

  // Fall back to static lookup
  return staticGetTagTheme(tagName)
}

/**
 * Computed properties for filtered tag lists
 */
const artTags = computed(() =>
  tags.value.filter(tag => tag.theme === 'art')
)

const scienceTags = computed(() =>
  tags.value.filter(tag => tag.theme === 'science')
)

const tagCount = computed(() => ({
  total: tags.value.length,
  art: artTags.value.length,
  science: scienceTags.value.length
}))

/**
 * Main composable for consumers - read-only access to tags
 */
export function useTags() {
  // Auto-load tags on first use
  if (!isLoaded.value && !isLoading.value) {
    loadTags()
  }

  return {
    // Reactive state (readonly to prevent accidental mutations)
    tags: readonly(tags),
    artTags: readonly(artTags),
    scienceTags: readonly(scienceTags),
    tagCount: readonly(tagCount),
    isLoading: readonly(isLoading),
    isLoaded: readonly(isLoaded),
    loadError: readonly(loadError),
    usingFallback: readonly(usingFallback),

    // Functions
    loadTags,
    getTagTheme
  }
}

// ==================== ADMIN FUNCTIONS ====================

/**
 * Save current tags state to Firestore
 */
async function saveTagsToFirestore(adminUid) {
  const docRef = doc(db, TAGS_DOC_PATH)
  await setDoc(docRef, {
    tags: tags.value,
    lastUpdatedAt: serverTimestamp(),
    lastUpdatedBy: adminUid || 'unknown'
  })
  return true
}

/**
 * Add a new tag
 */
async function addTag(name, theme, adminUid) {
  // Validate inputs
  if (!name || typeof name !== 'string') {
    throw new Error('Tag name is required')
  }
  if (!['art', 'science'].includes(theme)) {
    throw new Error('Theme must be "art" or "science"')
  }

  const trimmedName = name.trim()

  // Check for duplicates (case-insensitive)
  const exists = tags.value.some(t =>
    t.name.toLowerCase() === trimmedName.toLowerCase()
  )
  if (exists) {
    throw new Error(`Tag "${trimmedName}" already exists`)
  }

  // Add tag with metadata
  const newTag = {
    name: trimmedName,
    theme,
    createdAt: new Date().toISOString()
  }

  // Update local state
  tags.value = [...tags.value, newTag].sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  )

  // Persist to Firestore
  await saveTagsToFirestore(adminUid)
  usingFallback.value = false

  return newTag
}

/**
 * Remove a tag by name
 */
async function removeTag(name, adminUid) {
  if (!name || typeof name !== 'string') {
    throw new Error('Tag name is required')
  }

  const lowerName = name.toLowerCase()
  const index = tags.value.findIndex(t => t.name.toLowerCase() === lowerName)

  if (index === -1) {
    throw new Error(`Tag "${name}" not found`)
  }

  // Update local state
  tags.value = tags.value.filter((_, i) => i !== index)

  // Persist to Firestore
  await saveTagsToFirestore(adminUid)

  return true
}

/**
 * Update a tag's properties
 */
async function updateTag(oldName, updates, adminUid) {
  if (!oldName || typeof oldName !== 'string') {
    throw new Error('Tag name is required')
  }

  const lowerOldName = oldName.toLowerCase()
  const index = tags.value.findIndex(t => t.name.toLowerCase() === lowerOldName)

  if (index === -1) {
    throw new Error(`Tag "${oldName}" not found`)
  }

  // If renaming, check for duplicates
  if (updates.name && updates.name.toLowerCase() !== lowerOldName) {
    const exists = tags.value.some(t =>
      t.name.toLowerCase() === updates.name.toLowerCase()
    )
    if (exists) {
      throw new Error(`Tag "${updates.name}" already exists`)
    }
  }

  // Update the tag
  const updatedTag = {
    ...tags.value[index],
    ...updates,
    updatedAt: new Date().toISOString()
  }

  tags.value = [
    ...tags.value.slice(0, index),
    updatedTag,
    ...tags.value.slice(index + 1)
  ].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  // Persist to Firestore
  await saveTagsToFirestore(adminUid)

  return updatedTag
}

/**
 * Seed Firestore with static tags (for initial setup)
 */
async function seedTagsFromStatic(adminUid) {
  // Add createdAt to all tags for consistency
  const seededTags = PREDEFINED_TAGS.map(tag => ({
    ...tag,
    createdAt: new Date().toISOString()
  }))

  tags.value = seededTags
  await saveTagsToFirestore(adminUid)
  usingFallback.value = false

  return seededTags
}

/**
 * Admin composable - includes write operations
 */
export function useTagsAdmin() {
  // Include all read operations
  const readOps = useTags()

  return {
    ...readOps,

    // Write operations
    addTag,
    removeTag,
    updateTag,
    seedTagsFromStatic,
    saveTagsToFirestore
  }
}

// Also export static arrays for backward compatibility during migration
export { PREDEFINED_TAGS, ART_TAGS, SCIENCE_TAGS }
